import type { SessionSource } from "@constants/tracking";
import { MicrophonePermissionDialog } from "@memorang/ui/components/MicroPhonePermissionDialog";
import { SessionReportNextActionDialog } from "@memorang/ui/components/SessionReport/SessionReportNextActionDialog";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { RecommendedSkillTask } from "../recommendations/adapters/dashboard-task-types";
import { buildRecommendedSessionDialogContent } from "../recommendations/adapters/recommended-session-dialog";
import { useAdaptiveSessionStart } from "./useAdaptiveSessionStart";

/**
 * Shows a dialog before starting a recommended study session.
 * Explains the session, then starts it. Shared by all entry points.
 */
export const useRecommendedSessionDialog = (source: SessionSource) => {
	const { t } = useTranslation();
	const [selectedTask, setSelectedTask] = useState<RecommendedSkillTask>();
	const [visible, setVisible] = useState(false);

	const {
		startAdaptiveSession,
		isCreatingAdaptiveSession,
		showMicPermissionDialog,
		setShowMicPermissionDialog,
	} = useAdaptiveSessionStart();

	const content = useMemo(
		() =>
			selectedTask
				? buildRecommendedSessionDialogContent(selectedTask, t)
				: null,
		[selectedTask, t],
	);

	const openRecommendedSession = (task: RecommendedSkillTask) => {
		if (!task.quickStudyMeta) {
			return;
		}
		setSelectedTask(task);
		setVisible(true);
	};

	const recommendedSessionDialog = (
		<>
			{selectedTask && content ? (
				<SessionReportNextActionDialog
					visible={visible}
					onClose={() => setVisible(false)}
					title={t("report.nextAction.dialog.title")}
					subtitle={t("report.nextAction.dialog.subtitle")}
					session={content.session}
					insight={content.insight}
					startLabel={t("sessionConfig.start")}
					onStart={() => startAdaptiveSession(selectedTask, source)}
					starting={isCreatingAdaptiveSession}
				/>
			) : null}
			<MicrophonePermissionDialog
				showDialog={showMicPermissionDialog}
				setShowDialog={() => setShowMicPermissionDialog(false)}
			/>
		</>
	);

	return {
		openRecommendedSession,
		recommendedSessionDialog,
		startAdaptiveSession,
		isCreatingAdaptiveSession,
	};
};
